import { CellRuleValue } from 'interfaces/cellRule';
import { CellTypingExecutionUnitId, PanelOrchestrationRequest } from 'interfaces/jobs/cellTypingMarkerPositivity';
import { MultiplexPanelView } from 'interfaces/multiplexPanels';
import type { CellTypingRequestedOutputs } from '.';
import {
  getSpotDetectionGeometryError,
  getSpotDetectionHScoreThresholds,
  getSpotDetectionScoringGeometry,
} from '../../PostProcessingAndCalculateFeatures/spotDetectionInputSetup';
import type { PanelConfigurationDraft } from './PanelConfigurationEditor';

export const scopePanelConfiguration = (
  panel: MultiplexPanelView,
  draft: PanelConfigurationDraft
): PanelConfigurationDraft => {
  const channelIds = new Set(panel.channels.flatMap((channel) => (channel.saved ? [channel.saved.analysisName] : [])));
  const included = <Value>(entries?: Record<string, Value>): Record<string, Value> =>
    Object.fromEntries(Object.entries(entries ?? {}).filter(([channelId]) => channelIds.has(channelId)));
  const cellRules = draft.cellRules.filter((rule) => channelIds.has(rule.stainTypeId));
  return {
    ...draft,
    markerThresholds: included(draft.markerThresholds),
    probeThresholds: included(draft.probeThresholds),
    probeThresholdLinks: included(draft.probeThresholdLinks),
    hScoreBinMaximumDrafts: included(draft.hScoreBinMaximumDrafts),
    perProbeSetup: included(draft.perProbeSetup),
    cellRules,
    cellTypeIds: draft.cellTypeIds.filter(
      (cellTypeId) =>
        !draft.cellRules.some((rule) => rule.cellTypeId === cellTypeId) ||
        cellRules.some((rule) => rule.cellTypeId === cellTypeId)
    ),
  };
};

export interface ExecutionUnitChoice {
  id: CellTypingExecutionUnitId;
  label: string;
  applicable: boolean;
  reason: string;
}

const UNIT_LABELS: Record<CellTypingExecutionUnitId, string> = {
  binary_classification: 'Marker probabilities',
  calculate_thresholds: 'Thresholding',
  cell_typing: 'Cell typing',
  calculate_features: 'RNA counts and scores',
};

const savedIdentities = (panel: MultiplexPanelView, modality: 'mif' | 'rna') =>
  panel.channels.flatMap((channel) =>
    channel.saved?.channelModality === modality ? [channel.saved.analysisName] : []
  );

const finiteThresholds = (values: Record<string, string>, markerIds: string[]) => {
  const parsed: Record<string, number> = {};
  for (const markerId of markerIds) {
    const value = values[markerId];
    if (value === undefined || value === '' || !Number.isFinite(Number(value))) continue;
    parsed[markerId] = Number(value);
  }
  return parsed;
};

const hasFiniteCutoff = (values: Record<string, string>, markerId: string) => {
  const value = values[markerId];
  return value !== undefined && value !== '' && Number.isFinite(Number(value));
};

export const estimatesMifCutoffsInRun = (
  thresholdSource: 'saved' | 'estimate',
  markerThresholds: Record<string, string>,
  constrainedMarkerIds: string[]
) =>
  thresholdSource === 'estimate' ||
  constrainedMarkerIds.some((markerId) => !hasFiniteCutoff(markerThresholds, markerId));

const withEstimatedCutoffUnits = (
  units: CellTypingExecutionUnitId[],
  estimatesCutoffs: boolean,
  mifMarkerIds: Set<string>
): CellTypingExecutionUnitId[] => {
  if (!estimatesCutoffs || !mifMarkerIds.size) return units;
  const selected = new Set<CellTypingExecutionUnitId>([...units, 'binary_classification', 'calculate_thresholds']);
  return (['binary_classification', 'calculate_thresholds', 'cell_typing', 'calculate_features'] as const).filter(
    (unitId) => selected.has(unitId)
  );
};

const groupedRules = (draft: PanelConfigurationDraft, markerIds: Set<string>): PanelOrchestrationRequest['cellRules'] =>
  draft.cellTypeIds.flatMap((targetCellTypePath) => {
    const rules = Object.fromEntries(
      draft.cellRules
        .filter((rule) => rule.cellTypeId === targetCellTypePath && markerIds.has(rule.stainTypeId))
        .map((rule) => [rule.stainTypeId, rule.ruleValue])
    );
    return Object.values(rules).some((ruleValue) => ruleValue !== CellRuleValue.Indifferent)
      ? [{ targetCellTypePath, rules }]
      : [];
  });

const ruleSignature = (rules: Record<string, number>) =>
  Object.entries(rules)
    .filter(([, ruleValue]) => ruleValue !== CellRuleValue.Indifferent)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([markerId, ruleValue]) => `${markerId}:${ruleValue}`)
    .join('|');

export const preparedCellRules = (
  draft: PanelConfigurationDraft,
  mifMarkerIds: Set<string>,
  rnaProbeIds: Set<string>,
  enabled = true
) => {
  if (!enabled) return { cellRules: [], rnaCellRules: [], error: null };
  const mifRules = groupedRules(draft, mifMarkerIds);
  const rnaRules = groupedRules(draft, rnaProbeIds);
  const rnaTargets = new Set(rnaRules.map((rule) => rule.targetCellTypePath));
  const pureMifRules = mifRules.filter((rule) => !rnaTargets.has(rule.targetCellTypePath));
  const mixedMifRules = mifRules.filter((rule) => rnaTargets.has(rule.targetCellTypePath));
  const sourceBySignature = new Map<string, string>();
  for (const rule of pureMifRules) {
    const signature = ruleSignature(rule.rules);
    if (sourceBySignature.has(signature)) {
      return {
        cellRules: [],
        rnaCellRules: [],
        error: 'Two mIF cell types use the same marker rules.',
      };
    }
    sourceBySignature.set(signature, rule.targetCellTypePath);
  }
  const rnaCellRules = [];
  for (const rule of rnaRules) {
    const mifRule = mixedMifRules.find((item) => item.targetCellTypePath === rule.targetCellTypePath);
    if (!mifRule) {
      rnaCellRules.push(rule);
      continue;
    }
    const sourceCellTypePath = sourceBySignature.get(ruleSignature(mifRule.rules));
    if (!sourceCellTypePath) {
      return {
        cellRules: [],
        rnaCellRules: [],
        error: 'A cell type that uses both mIF and RNA must match one mIF cell type.',
      };
    }
    rnaCellRules.push({ ...rule, sourceCellTypePath });
  }
  return { cellRules: pureMifRules, rnaCellRules, error: null as string | null };
};

const rnaConfiguration = (
  panel: MultiplexPanelView,
  draft: PanelConfigurationDraft,
  requestedOutputs: CellTypingRequestedOutputs,
  includedProbeIds?: string[]
): PanelOrchestrationRequest['rnaConfiguration'] | null => {
  const probes: NonNullable<PanelOrchestrationRequest['rnaConfiguration']>['probes'] = [];
  for (const channel of panel.channels) {
    const saved = channel.saved;
    if (saved?.channelModality !== 'rna') continue;
    if (includedProbeIds && !includedProbeIds.includes(saved.analysisName)) continue;
    const setup = { ...draft.spotDetectionSetup, ...draft.perProbeSetup[saved.analysisName] };
    const geometry = getSpotDetectionScoringGeometry(setup);
    const threshold = Number(draft.probeThresholds[saved.analysisName]);
    if (
      getSpotDetectionGeometryError(setup) ||
      (geometry.compartment === 'cell' && setup.segmentationDilationsUm?.length !== 1) ||
      !Number.isFinite(threshold) ||
      threshold < 0
    ) {
      return null;
    }
    probes.push({
      probeId: saved.analysisName,
      positivityThreshold: threshold,
      ...geometry,
    });
  }
  if (!probes.length) return null;
  const rnaProbeIds = new Set(probes.map((probe) => probe.probeId));
  return {
    probes,
    cellRules: requestedOutputs.cellTypes ? groupedRules(draft, rnaProbeIds) : [],
  };
};

export const executionChoicesForPanel = ({
  panel,
  draft,
  requestedOutputs,
  includedMifMarkerIds,
  includedRnaProbeIds,
  thresholdSource = 'saved',
}: {
  panel: MultiplexPanelView;
  draft?: PanelConfigurationDraft;
  requestedOutputs: CellTypingRequestedOutputs;
  includedMifMarkerIds?: string[];
  includedRnaProbeIds?: string[];
  thresholdSource?: 'saved' | 'estimate';
}): ExecutionUnitChoice[] => {
  const includedMif = new Set(includedMifMarkerIds);
  const includedRna = new Set(includedRnaProbeIds);
  const mifMarkerIds = savedIdentities(panel, 'mif').filter(
    (markerId) => includedMifMarkerIds === undefined || includedMif.has(markerId)
  );
  const rnaProbeIds = savedIdentities(panel, 'rna').filter(
    (probeId) => includedRnaProbeIds === undefined || includedRna.has(probeId)
  );
  const hasOutput = requestedOutputs.positivity || requestedOutputs.cellTypes;
  const outputReason = hasOutput ? '' : 'Select positivity or cell typing before running a unit.';
  const mifReason = mifMarkerIds.length ? '' : 'No mIF markers on the selected slides.';
  const prepared = draft
    ? preparedCellRules(draft, new Set(mifMarkerIds), new Set(rnaProbeIds), requestedOutputs.cellTypes)
    : null;
  const activeMifRules = prepared ? prepared.cellRules.flatMap((rule) => Object.entries(rule.rules)) : [];
  const hasOneOfTwo = activeMifRules.some(([, ruleValue]) => ruleValue === CellRuleValue.OneOfTwoMustBePositive);
  const constrainedMifMarkers = [
    ...new Set(
      activeMifRules.filter(([, ruleValue]) => ruleValue !== CellRuleValue.Indifferent).map(([markerId]) => markerId)
    ),
  ];
  const thresholds = finiteThresholds(draft?.markerThresholds ?? {}, constrainedMifMarkers);
  const missingThresholds = constrainedMifMarkers.filter((markerId) => thresholds[markerId] === undefined);
  const rnaConfig = draft ? rnaConfiguration(panel, draft, requestedOutputs, rnaProbeIds) : null;
  const needsCellRule = requestedOutputs.cellTypes && !prepared?.rnaCellRules.length && !constrainedMifMarkers.length;
  const preparedError = prepared?.error;
  const estimatesCutoffs = estimatesMifCutoffsInRun(
    thresholdSource,
    draft?.markerThresholds ?? {},
    constrainedMifMarkers
  );
  let cellTypingReason = 'Uses panel thresholds.';
  if (!requestedOutputs.cellTypes) cellTypingReason = 'Turn on cell types to assign cell classes.';
  else if (!hasOutput) cellTypingReason = outputReason;
  else if (preparedError) cellTypingReason = preparedError;
  else if (mifReason) cellTypingReason = mifReason;
  else if (hasOneOfTwo) cellTypingReason = 'Cell typing accepts only indifferent, negative, or positive rules.';
  else if (!constrainedMifMarkers.length) cellTypingReason = 'Cell typing requires an active mIF cell rule.';
  else if (estimatesCutoffs) {
    cellTypingReason = 'Uses calculated mIF thresholds.';
  } else if (missingThresholds.length) {
    cellTypingReason = `Enter a threshold for each mIF marker used by a cell rule: ${missingThresholds.join(', ')}.`;
  }

  return [
    {
      id: 'binary_classification',
      label: UNIT_LABELS.binary_classification,
      applicable: Boolean(hasOutput && mifMarkerIds.length),
      reason: !hasOutput ? outputReason : mifReason || 'Produces marker probabilities for the selected slides.',
    },
    {
      id: 'calculate_thresholds',
      label: UNIT_LABELS.calculate_thresholds,
      applicable: Boolean(hasOutput && mifMarkerIds.length),
      reason: !hasOutput
        ? outputReason
        : mifReason || 'Uses new probabilities when binary classification is selected; otherwise the latest artifacts.',
    },
    {
      id: 'cell_typing',
      label: UNIT_LABELS.cell_typing,
      applicable: Boolean(
        requestedOutputs.cellTypes &&
          hasOutput &&
          !preparedError &&
          mifMarkerIds.length &&
          constrainedMifMarkers.length &&
          !hasOneOfTwo &&
          (estimatesCutoffs || !missingThresholds.length)
      ),
      reason: cellTypingReason,
    },
    {
      id: 'calculate_features',
      label: UNIT_LABELS.calculate_features,
      applicable: Boolean(hasOutput && rnaProbeIds.length && rnaConfig && !needsCellRule && !preparedError),
      reason: !hasOutput
        ? outputReason
        : preparedError
        ? preparedError
        : !rnaProbeIds.length
        ? 'RNA counts require an included RNA probe.'
        : !rnaConfig
        ? 'Enter one count geometry and a finite positivity threshold for each RNA probe.'
        : needsCellRule
        ? 'Select at least one cell rule for cell typing.'
        : 'Counts existing detections, then positivity, phenotypes, and H-scores.',
    },
  ];
};

const thresholdFlowPayload = (draft: PanelConfigurationDraft): PanelOrchestrationRequest['thresholdFlow'] => {
  const flow = draft.thresholdFlow;
  if (!flow) return undefined;
  const rangeStart = Number(flow.rangeStart);
  const rangeEnd = Number(flow.rangeEnd);
  const maxThreshold = Number(flow.maxThreshold);
  const precisionDecimal = Number(flow.precisionDecimal);
  if (
    ![rangeStart, rangeEnd, maxThreshold, precisionDecimal].every(Number.isFinite) ||
    rangeStart >= rangeEnd ||
    rangeStart < 0 ||
    rangeEnd > 1 ||
    maxThreshold < 0 ||
    maxThreshold > 1 ||
    !Number.isInteger(precisionDecimal) ||
    precisionDecimal < 0 ||
    precisionDecimal > 10
  ) {
    return undefined;
  }
  return { rangeStart, rangeEnd, maxThreshold, precisionDecimal };
};

export const orchestrationSubmitBlockReason = ({
  panel,
  draft,
  targetSlideIds,
  requestedOutputs,
  executionUnits,
  includedMifMarkerIds,
  includedRnaProbeIds,
  thresholdSource = 'saved',
}: {
  panel: MultiplexPanelView;
  draft: PanelConfigurationDraft;
  targetSlideIds: string[];
  requestedOutputs: CellTypingRequestedOutputs;
  executionUnits: CellTypingExecutionUnitId[];
  includedMifMarkerIds?: string[];
  includedRnaProbeIds?: string[];
  thresholdSource?: 'saved' | 'estimate';
}): string | null => {
  if (!panel.revision) return 'Save the panel definition before running it.';
  if (!targetSlideIds.length) return 'Select at least one slide in this panel.';
  if (!executionUnits.length) return 'Select a step before running this panel.';
  if (executionUnits.includes('calculate_thresholds') && draft.thresholdFlow && !thresholdFlowPayload(draft)) {
    return 'Fix the cutoff estimate settings before running this panel.';
  }
  const panelMifIds = savedIdentities(panel, 'mif');
  const panelRnaIds = savedIdentities(panel, 'rna');
  const prepared = preparedCellRules(
    draft,
    new Set(
      includedMifMarkerIds ? panelMifIds.filter((markerId) => includedMifMarkerIds.includes(markerId)) : panelMifIds
    ),
    new Set(includedRnaProbeIds ? panelRnaIds.filter((probeId) => includedRnaProbeIds.includes(probeId)) : panelRnaIds),
    requestedOutputs.cellTypes
  );
  if (prepared.error) return prepared.error;
  const needsMifLabels =
    prepared.cellRules.some((rule) =>
      Object.values(rule.rules).some((ruleValue) => ruleValue !== CellRuleValue.Indifferent)
    ) || prepared.rnaCellRules.some((rule) => 'sourceCellTypePath' in rule && Boolean(rule.sourceCellTypePath));
  if (
    requestedOutputs.cellTypes &&
    needsMifLabels &&
    executionUnits.includes('calculate_features') &&
    !executionUnits.includes('cell_typing')
  ) {
    return 'These cell types use mIF markers. Run cell typing in the same request.';
  }
  if (
    !orchestrationRequestForPanel({
      panel,
      draft,
      targetSlideIds,
      requestedOutputs,
      executionUnits,
      includedMifMarkerIds,
      includedRnaProbeIds,
      thresholdSource,
    })
  ) {
    return 'This panel cannot be submitted yet.';
  }
  return null;
};

export const orchestrationRequestForPanel = ({
  panel,
  draft,
  targetSlideIds,
  requestedOutputs,
  executionUnits,
  includedMifMarkerIds,
  includedRnaProbeIds,
  thresholdSource = 'saved',
  useAnalysisAreas = false,
}: {
  panel: MultiplexPanelView;
  draft: PanelConfigurationDraft;
  targetSlideIds: string[];
  requestedOutputs: CellTypingRequestedOutputs;
  executionUnits: CellTypingExecutionUnitId[];
  includedMifMarkerIds?: string[];
  includedRnaProbeIds?: string[];
  thresholdSource?: 'saved' | 'estimate';
  useAnalysisAreas?: boolean;
}): PanelOrchestrationRequest | null => {
  if (!panel.revision || !targetSlideIds.length || !executionUnits.length) return null;
  if (executionUnits.includes('calculate_thresholds') && draft.thresholdFlow && !thresholdFlowPayload(draft))
    return null;
  const panelMifIds = savedIdentities(panel, 'mif');
  const panelRnaIds = savedIdentities(panel, 'rna');
  const mifMarkerIds = new Set(
    includedMifMarkerIds ? panelMifIds.filter((markerId) => includedMifMarkerIds.includes(markerId)) : panelMifIds
  );
  const rnaProbeIds = new Set(
    includedRnaProbeIds ? panelRnaIds.filter((probeId) => includedRnaProbeIds.includes(probeId)) : panelRnaIds
  );
  const prepared = preparedCellRules(draft, mifMarkerIds, rnaProbeIds, requestedOutputs.cellTypes);
  if (prepared.error) return null;
  const constrainedMifMarkers = [
    ...new Set(
      prepared.cellRules.flatMap((rule) =>
        Object.entries(rule.rules)
          .filter(([, ruleValue]) => ruleValue !== CellRuleValue.Indifferent)
          .map(([markerId]) => markerId)
      )
    ),
  ];
  const estimatesCutoffs = estimatesMifCutoffsInRun(thresholdSource, draft.markerThresholds, constrainedMifMarkers);
  const units = withEstimatedCutoffUnits(executionUnits, estimatesCutoffs, mifMarkerIds);
  if (!units.length) return null;
  const rnaConfig = units.includes('calculate_features')
    ? rnaConfiguration(panel, draft, requestedOutputs, [...rnaProbeIds])
    : undefined;
  if (units.includes('calculate_features') && !rnaConfig) return null;
  const includedRnaConfig = rnaConfig
    ? {
        probes: rnaConfig.probes.filter((probe) => rnaProbeIds.has(probe.probeId)),
        cellRules: requestedOutputs.cellTypes
          ? prepared.rnaCellRules.flatMap((cellRule) => {
              const rules = Object.fromEntries(
                Object.entries(cellRule.rules).filter(([probeId]) => rnaProbeIds.has(probeId))
              );
              return Object.values(rules).some((ruleValue) => ruleValue !== CellRuleValue.Indifferent)
                ? [{ ...cellRule, rules }]
                : [];
            })
          : [],
      }
    : undefined;
  if (units.includes('calculate_features') && !includedRnaConfig?.probes.length) return null;
  const hScoreMinimumCountsByProbe = Object.fromEntries(
    Object.entries(draft.perProbeSetup).flatMap(([probeId, setup]) =>
      rnaProbeIds.has(probeId) && setup?.countBins?.length
        ? [[probeId, getSpotDetectionHScoreThresholds(setup.countBins)]]
        : []
    )
  );
  return {
    panelId: panel.panelId,
    revision: panel.revision,
    targetSlideIds,
    knnTrainingScope: draft.knnTrainingScope,
    executionUnits: units,
    markerThresholds: estimatesCutoffs ? {} : finiteThresholds(draft.markerThresholds, [...mifMarkerIds]),
    cellRules: requestedOutputs.cellTypes ? prepared.cellRules : [],
    ...(includedRnaConfig ? { rnaConfiguration: includedRnaConfig } : {}),
    requestedOutputs,
    useAnalysisAreas,
    ...(units.includes('calculate_thresholds') && thresholdFlowPayload(draft)
      ? { thresholdFlow: thresholdFlowPayload(draft) }
      : {}),
    thresholdSource: estimatesCutoffs ? 'estimate' : thresholdSource,
    includedMifMarkerIds: [...mifMarkerIds],
    includedRnaProbeIds: [...rnaProbeIds],
    hScoreMinimumCounts: getSpotDetectionHScoreThresholds(draft.spotDetectionSetup.countBins),
    ...(Object.keys(hScoreMinimumCountsByProbe).length ? { hScoreMinimumCountsByProbe } : {}),
  };
};
